import { useEffect } from 'react'
import { RefreshCw, Database, TrendingUp, AlertCircle, Check } from 'lucide-react'
import { useQueryLog } from '../../hooks/useQueryLog'

const ADMIN_EMAIL = 'test@sfpwealth.co.za'

export function SupabaseUsageDashboard({ userEmail }) {
  const { fetchUsage, usage, loading } = useQueryLog()
  const isAdmin = userEmail === ADMIN_EMAIL

  // Load usage on mount (only if admin)
  useEffect(() => {
    if (isAdmin) {
      fetchUsage()
    }
  }, [fetchUsage, isAdmin])

  // Only admin can view this
  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center p-12 rounded-lg border-2 border-red-200 bg-red-50">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-3" />
          <p className="text-red-700 font-semibold">Access Denied</p>
          <p className="text-sm text-red-600 mt-1">Only administrators can view Supabase usage.</p>
        </div>
      </div>
    )
  }

  if (loading && !usage) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <RefreshCw className="w-6 h-6 text-sfp-orange animate-spin mx-auto mb-3" />
          <p className="text-gray-600">Loading usage data...</p>
        </div>
      </div>
    )
  }

  if (!usage) {
    return (
      <div className="p-6 bg-gray-50 rounded-lg border border-gray-200 text-center">
        <p className="text-gray-600">No usage data available</p>
      </div>
    )
  }

  const topTable = usage.byTable.length > 0 ? usage.byTable[0][0] : 'N/A'
  const statusColor = usage.isHigh ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
  const statusIcon = usage.isHigh ? <AlertCircle className="w-4 h-4" /> : <Check className="w-4 h-4" />
  const statusLabel = usage.isHigh ? 'High (>50k)' : 'Normal'

  // Calculate max queries for bar chart scaling
  const maxQueries = Math.max(...usage.byTable.map(([, count]) => count), 1)

  return (
    <div className="space-y-8">
      {/* Header with Refresh Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-sfp-darkgray flex items-center gap-2">
            <Database className="w-6 h-6 text-sfp-orange" />
            Supabase Database Usage
          </h2>
          <p className="text-sm text-gray-600 mt-1">Current Month Query Statistics</p>
        </div>
        <button
          onClick={() => fetchUsage(true)}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-sfp-orange text-white rounded font-semibold hover:bg-orange-600 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* KPI Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Total Queries Tile */}
        <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                Total Queries
              </p>
              <p className="text-3xl font-bold mt-2 text-sfp-darkgray">
                {usage.totalQueries.toLocaleString()}
              </p>
              <p className="text-xs mt-2 text-gray-500">This month</p>
            </div>
            <Database className="w-8 h-8 text-sfp-orange" />
          </div>
        </div>

        {/* Estimated Cost Tile */}
        <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                Est. Cost (ZAR)
              </p>
              <p className="text-3xl font-bold mt-2 text-sfp-orange">
                R{parseFloat(usage.totalCostZAR).toFixed(2)}
              </p>
              <p className="text-xs mt-2 text-gray-500">
                ${parseFloat(usage.totalCostUSD).toFixed(4)}
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-sfp-orange" />
          </div>
        </div>

        {/* Status Indicator Tile */}
        <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                Status
              </p>
              <div className={`inline-flex items-center gap-2 mt-2 px-3 py-1 rounded-full text-sm font-semibold ${statusColor}`}>
                {statusIcon}
                {statusLabel}
              </div>
              <p className="text-xs mt-2 text-gray-500">
                {usage.isHigh ? 'Threshold exceeded' : 'Operating normally'}
              </p>
            </div>
            <AlertCircle className="w-8 h-8 text-sfp-orange" />
          </div>
        </div>

        {/* Top Table Tile */}
        <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                Top Table
              </p>
              <p className="text-xl font-bold mt-2 text-sfp-darkgray break-words">
                {topTable}
              </p>
              <p className="text-xs mt-2 text-gray-500">
                {usage.byTable.length > 0 ? `${usage.byTable[0][1]} queries` : 'No data'}
              </p>
            </div>
            <Database className="w-8 h-8 text-sfp-orange" />
          </div>
        </div>
      </div>

      {/* Table Breakdown Section */}
      <div className="rounded-xl p-6 shadow-md border bg-white border-gray-100">
        <h3 className="text-lg font-bold mb-6 flex items-center gap-2 text-sfp-darkgray">
          <Database className="w-5 h-5 text-sfp-orange" />
          Queries by Table
        </h3>

        {usage.byTable.length > 0 ? (
          <div className="space-y-4">
            {usage.byTable.map(([tableName, count]) => {
              const percentage = (count / maxQueries) * 100
              return (
                <div key={tableName}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold text-sfp-darkgray">{tableName}</span>
                    <span className="text-sm font-bold text-sfp-orange">{count}</span>
                  </div>
                  <div className="w-full rounded-full h-2 bg-gray-100">
                    <div
                      className="bg-gradient-to-r from-sfp-orange to-orange-600 h-2 rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No query data yet</p>
        )}
      </div>

      {/* Query Type Breakdown Section */}
      <div className="rounded-xl p-6 shadow-md border bg-white border-gray-100">
        <h3 className="text-lg font-bold mb-6 flex items-center gap-2 text-sfp-darkgray">
          <TrendingUp className="w-5 h-5 text-sfp-orange" />
          Query Types
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(usage.byType).map(([type, count]) => (
            <div key={type} className="bg-sfp-lightblue rounded-lg p-4 text-center">
              <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                {type}
              </p>
              <p className="text-2xl font-bold text-sfp-darkgray mt-2">{count}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Info Box */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
        <p className="font-semibold mb-1">ℹ️ About this dashboard</p>
        <p>
          Query logs are automatically tracked when hooks use the <code className="bg-blue-100 px-1 rounded">logQuery()</code> function.
          Data is cached for 30 seconds to avoid excessive reads. Cost estimate: <strong>R0.0016 per query</strong> (0.0001 USD × 16).
        </p>
      </div>
    </div>
  )
}
